import { mapApiImageToImageProps } from './common.mappers';

import type { FUseCaseTemplateProps } from '@/components/templates/FUseCaseTemplate.vue';

export function mapApiDataToUseCase(
  useCaseRawData: StrapiResponseData<UseCaseContentType> | undefined,
  useCasesCardsRawData: StrapiResponseData<UseCaseContentType>[] | undefined
): (FUseCaseTemplateProps & { redirect_url: string }) | null {
  const useCaseData = useCaseRawData?.attributes;
  const { t } = useI18n();
  if (!useCaseData) return null;

  return {
    redirect_url: useCaseData.redirect_url ?? '',
    colors: {
      primaryLight1: useCaseData.colors?.primary?.light_1,
      primaryLight2: useCaseData.colors?.primary?.light_2,
      primary: useCaseData.colors?.primary?.base,
      primaryDark1: useCaseData.colors?.primary?.dark_1,
      primaryDark2: useCaseData.colors?.primary?.dark_2,
      secondaryLight1: useCaseData.colors?.secondary?.light_1,
      secondaryLight2: useCaseData.colors?.secondary?.light_2,
      secondary: useCaseData.colors?.secondary?.base,
      secondaryDark1: useCaseData.colors?.secondary?.dark_1,
      secondaryDark2: useCaseData.colors?.secondary?.dark_2,
    },
    hero: {
      title: useCaseData.hero?.title ?? '',
      description: useCaseData.hero?.description ?? '',
      image: mapApiImageToImageProps(
        useCaseData?.hero?.image_background?.data?.attributes
      ),
    },
    parallaxBikeImage: mapApiImageToImageProps(
      useCaseData.hero?.image_bike?.data?.attributes
    ),
    parallaxPhoneImage: mapApiImageToImageProps(
      useCaseData.hero?.image_app?.data?.attributes
    ),
    testimonial: {
      videoId: useCaseData.testimonial?.youtube_video_id ?? '',
      videoStartTime: useCaseData.testimonial?.youtube_video_start_time ?? 0,
      placeholderImageSrc: mapApiImageToImageProps(
        useCaseData.testimonial?.thumbnail_image?.data?.attributes
      ).src,
      quote: useCaseData.testimonial?.quote_text ?? '',
      quoteAuthor: useCaseData.testimonial?.quote_author_name ?? '',
      quoteAuthorTitle: useCaseData.testimonial?.quote_author_title ?? '',
      quoteAuthorAvatarImage: mapApiImageToImageProps(
        useCaseData.testimonial?.quote_author_avatar?.data?.attributes
      ),
    },
    kpisSection: {
      headline: useCaseData.kpis_with_headline?.headline ?? '',
      kpis: (useCaseData.kpis_with_headline?.kpis ?? []).map(kpi => ({
        value: kpi.value ?? 0,
        caption: kpi.caption ?? '',
        suffix: kpi.suffix ?? '',
        prefix: kpi.prefix ?? '',
      })),
    },
    appSolutionsText: useCaseData.app_solutions_text ?? '',
    downloadForm: {
      formId: useCaseData.contact_form?.hubspot_form_id ?? '',
      pdfUrl: useCaseData.contact_form?.pdf_url ?? '',
    },
    useCaseCards: (useCasesCardsRawData ?? [])
      ?.sort((a, b) => (a.attributes?.rank ?? 0) - (b.attributes?.rank ?? 0))
      .map(useCaseCard => ({
        categoryName: mapApiCategoriesToCategoryProps(
          useCaseCard?.attributes?.categories,
          useCaseCard?.attributes?.forced_augmented_bike_network ?? false
        ),
        title: useCaseCard?.attributes?.title_short ?? '',
        cityName: useCaseCard?.attributes?.city_name ?? '',
        image: mapApiImageToImageProps(
          useCaseCard?.attributes?.lifestyle_image?.data?.attributes
        ),
        link: {
          location:
            useCaseCard?.attributes?.redirect_url ??
            `/case-studies/${useCaseCard?.attributes?.slug ?? ''}`,
          text: t('use_case__card_link_text'),
        },
      })),
    category: mapApiCategoriesToCategoryProps(
      useCaseData.categories,
      useCaseData.forced_augmented_bike_network ?? false
    ),
  };
}

/**
 *  Returns the category of the use case based on the categories array
 */
export function mapApiCategoriesToCategoryProps(
  categories: ServiceCategory[],
  forceAugmentedBikeNetwork: boolean
): ServiceCategory | 'augmented_bike_network' {
  if (categories.length > 1 || forceAugmentedBikeNetwork) {
    return 'augmented_bike_network';
  }

  return categories[0] satisfies ServiceCategory;
}
